import { useContext, useState, useEffect, act } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";
import MUIButton from "@mui/material/Button";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIGrid from "@mui/material/Grid";
import MUIBox from "@mui/material/Box";
import MUIChip from "@mui/material/Chip";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIFormControl from "@mui/material/FormControl";
import MUIFormLabel from "@mui/material/FormLabel";
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUIRadio from "@mui/material/Radio";
import MUITypography from "@mui/material/Typography";
import MUICheckCircleIcon from "@mui/icons-material/CheckCircle";
import MUICancelIcon from "@mui/icons-material/Cancel";
import MUIHelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MUIDoneAllIcon from "@mui/icons-material/DoneAll";
import MUIUnpublishedIcon from "@mui/icons-material/Unpublished";
import MUIAlert from "@mui/material/Alert";
import Card from "../../Card";
import InfoItem from "../../InfoItem";
import Term from "../../../Utility/Crud/Term";
import Location from "../../../Utility/Crud/Location";
import Patient from "../../../Utility/Crud/Patient";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Product from "../../../Utility/Crud/Product";
import Security from "../../../Utility/Security";
import api from "../../../Seating/Security/api";

/**
 * CardChartApproval renders a Card component displaying order approval
 * information.
 * @returns {JSX.Element}
 */

const title = "Approval";
const icon = MUIDoneAllIcon;

const className = "CardChartApproval";

export default function CardChartApproval(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);

  const [approveClicked, setApproveClicked] = useState(false);
  const [unapproveClicked, setUnapproveClicked] = useState(false);

  const [newNoteId, setNewNoteId] = useState(null);
  const [newApprovalNoteId, setNewApprovalNoteId] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);

  const patient = chartContext.chart.patient[chartContext.patientId];
  const order = chartContext.chart.orderActivity[chartContext.orderId];
  const orderActivityApprovals = Object.values(
    chartContext.chart.orderActivityApprovals,
  ).find(
    (orderActivityApproval) =>
      orderActivityApproval.orderActivityId === chartContext.orderId,
  );

  const approval = Object.values(chartContext.chart?.approval || {}).find(
    (approval) => approval?.id === orderActivityApprovals?.approvalsId,
  );

  const billingNote = Object.values(chartContext.chart?.note || {})
    .filter(
      (note) =>
        note.noteTypeTermId === 88 && note.activityId === chartContext.orderId,
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const originalOrderActivity =
    chartContext.originalChart.orderActivity[chartContext.orderId];
  const originalApproval = chartContext.originalChart.approval[approval?.id];
  const originalBillingNote = chartContext.originalChart.note[billingNote?.id];

  const showBillingLocation = props.showBillingLocation ?? true;
  const showPlaceOfService = props.showPlaceOfService ?? true;
  const showCommissionTier = props.showCommissionTier ?? true;
  const showAuthNumber = props.showAuthNumber ?? true;
  const showPredeterminationNumber = props.showPredeterminationNumber ?? true;
  const showRentalOrSameSim = props.showRentalOrSameSim ?? true;
  const showFinancialHardship = props.showFinancialHardship ?? true;
  const showRentalOrPurchase = props.showRentalOrPurchase ?? true;
  const showBillingNote = props.showBillingNote ?? true;
  const allowApproval = props.allowApproval ?? true;
  const allowApprovalOnNoApprovedProducts =
    props.allowApprovalOnNoApprovedProducts ?? true;
  const showBaseCushionUpgrade = props.showBaseCushionUpgrade ?? true;
  const showEvaluationType = props.showEvaluationType ?? true;

  const validationRules = {
    key: className,
    rules: [
      {
        target: "orderActivity",
        id: chartContext.orderId,
        field: "billingLocationId",
        rule: { required: showBillingLocation },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "authNumber",
        rule: {
          required: showAuthNumber && !approval?.predeterminationNumber?.trim(),
        },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "placeOfServiceTermId",
        rule: { required: showPlaceOfService },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "commissionTierTermId",
        rule: { required: showCommissionTier },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "activeRentalSameOrSimilar",
        rule: { required: showRentalOrSameSim },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "financialHardship",
        rule: { required: showFinancialHardship },
      },
      {
        target: "approval",
        id: approval?.id,
        field: "rental",
        rule: { required: showRentalOrPurchase },
      },
    ],
  };

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
    setUnapproveClicked(false);
    setApproveClicked(false);
  }, [chartContext.originalChart]);

  // Get a new negative id for the billing note only once when the component mounts
  useEffect(() => {
    setNewNoteId(chartContext.getNegativeId());
    setNewApprovalNoteId(chartContext.getNegativeId());
  }, []);
  // Approval validation is handled explicitly on Approve click only.
  // Keep chart-level validation rules empty so normal Save does not require these fields.
  useEffect(() => {
    chartContext.updateValidationRules({ key: className, rules: [] });
  }, []);

  // Create a note if the order is approved
  useEffect(() => {
    if (approveClicked) {
      async function handleSaveChart() {
        await chartContext.handleSaveChart();
        api
          .send("GET", "/syncPatientToBt/" + chartContext.patientId)
          .catch((error) =>
            console.error("Unable to sync patient to Brightree.", error),
          );
      }
      handleSaveChart();
    }
  }, [approveClicked]);

  // Handle unapprove click - remove approval date and save
  useEffect(() => {
    if (unapproveClicked) {
      async function handleSaveChart() {
        await chartContext.handleSaveChart();
      }
      handleSaveChart();
    }
  }, [unapproveClicked]);

  // Open edit mode if validation issues are present
  // and show fields with validation issues
  useEffect(() => {
    if (chartContext.validationIssues[className]) {
      setEditMode(true);
    }
  }, [chartContext.validationIssues[className]]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Gets all current products tied to an order (Service and Regular Products).
   * @returns {Object} Object containing all service parts and order products
   */
  const getProductsOnCurrentOrder = () => {
    const orderProducts = OrderActivity.getProducts(
      order,
      chartContext.chart,
    ).filter((product) => product.approved === true);
    const serviceQuote = structuredClone(
      Object.values(chartContext.chart.serviceQuote || {}).find(
        (serviceQuote) => serviceQuote.orderId === chartContext.orderId,
      ),
    );

    let serviceParts = [];
    if (serviceQuote?.documentData?.length > 0) {
      serviceQuote.documentData = JSON.parse(serviceQuote.documentData);

      // Build parts array with only necessary fields
      serviceParts = serviceQuote.documentData.map((documentDatum) => {
        const catalogItem =
          chartContext.chart.servicePartCatalog[documentDatum.ItemId] || {};
        return {
          servicePartCatalogCategoryTermId:
            catalogItem.servicePartCatalogCategoryTermId,
          name: catalogItem.description,
          hcpcs: catalogItem.hcpc,
          quantity: documentDatum.Qty,
          warranty: documentDatum.Warranty,
          thirtyDayFit: documentDatum.ThirtyDayFit,
          abnRequired: documentDatum.AbnRequired,
          detail: documentDatum.Reason,
        };
      });
    }
    const allServiceParts = serviceParts;

    return { allServiceParts, orderProducts };
  };

  /**
   * Builds noteText for approval note depending on certain criteria.
   * @param {*} products
   * @returns {string} note text
   */
  const buildNoteText = (products) => {
    const allServiceParts = products.allServiceParts
      .map((product) => `${product.name} (${product.quantity})`)
      .join("\n");
    const orderProductsWithCalculatedPoints = products.orderProducts.map(
      (product) => {
        const points = Product.getPoints(
          mainContext.productSubTypesIndexed[product.productSubTypeId],
          order.insuranceTypeId,
          mainContext.insuranceMatrixEntriesIndexed,
          mainContext.productMatrixEntriesIndexed,
          product,
        );
        return { product, points: Number(points) };
      },
    );
    const orderProductsWithPoints = orderProductsWithCalculatedPoints.filter(
      ({ points }) => points > 0,
    );
    const orderProductLines = orderProductsWithPoints.map(
      ({ product, points }) => {
        const name = OrderActivity.getProductDisplayName(
          product,
          mainContext.baseUnitsIndexed,
          mainContext.vendorsIndexed,
          mainContext.productSubTypesIndexed,
        );
        return `${name} • x${product.qty} • ${points.toFixed(2)}pts`;
      },
    );
    const zeroPointProducts = orderProductsWithCalculatedPoints.filter(
      ({ points }) => points === 0,
    );
    const productTypeCounts = zeroPointProducts.reduce(
      (counts, { product }) => {
        const productTypeName =
          mainContext.productTypesIndexed[product.productTypeId].name;
        counts.set(productTypeName, (counts.get(productTypeName) || 0) + 1);
        return counts;
      },
      new Map(),
    );
    const productTypeLines = Array.from(
      productTypeCounts,
      ([productTypeName, count]) => `+${count} ${productTypeName}`,
    );
    const orderProductNames = [...orderProductLines, ...productTypeLines].join(
      "\n",
    );
    let orderTypeString;
    switch (order.orderType.toLowerCase()) {
      case "new":
      case "order":
        orderTypeString = "Order";
        break;
      case "billing issue":
      case "service":
      case "pickup":
      case "ticket":
        orderTypeString = "Ticket";
        break;
      default:
        orderTypeString = "Order";
    }
    let noteText = `Approved ${order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)} ${orderTypeString}`;

    if (approval?.authNumber) {
      noteText += ` with Auth # ${approval.authNumber}`;
    }
    if (orderProductNames.length > 0) {
      noteText += `\n\nProducts:\n${orderProductNames}`;
    }
    if (allServiceParts.length > 0) {
      noteText += `\n\nService Parts:\n${allServiceParts}`;
    }
    return noteText;
  };
  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  const renderBillingLocationDropdown = () => {
    const getBillingLocationOptions = () => {
      return Location.sort(Object.values(mainContext.locationsIndexed)).map(
        (location) => ({
          label: location.name,
          value: location.id,
        }),
      );
    };

    return (
      showBillingLocation && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIAutocomplete
            options={getBillingLocationOptions()}
            value={
              getBillingLocationOptions().find(
                (option) => option.value === order?.billingLocationId,
              ) || null
            }
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              chartContext.onChange({
                orderActivity: {
                  [chartContext.orderId]: {
                    billingLocationId: newValue?.value || null,
                  },
                },
              });
              setCardChart({
                ...cardChart,
                billingLocationId: newValue?.value || null,
              });
            }}
            disabled={originalOrderActivity?.isApproved === true}
            renderInput={(params) => (
              <MUITextField
                {...params}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="standard"
                label="Billing Location"
                fullWidth
                error={
                  chartContext.validationIssues[className]?.orderActivity?.[
                    chartContext.orderId
                  ]?.billingLocationId
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]?.orderActivity?.[
                    chartContext.orderId
                  ]?.billingLocationId
                    ? chartContext.validationIssues[className]?.orderActivity?.[
                        chartContext.orderId
                      ]?.billingLocationId
                    : ""
                }
              />
            )}
          />
        </MUIGrid>
      )
    );
  };

  const renderPlaceOfServiceDropdown = () => {
    const getPlaceOfServiceOptions = () => {
      return Term.sort(Object.values(mainContext.termsIndexed))
        .filter((term) => term.type === "place_of_service")
        .map((term) => ({
          label: term.name,
          value: term.id,
        }));
    };

    return (
      showPlaceOfService && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIAutocomplete
            options={getPlaceOfServiceOptions()}
            value={
              getPlaceOfServiceOptions().find(
                (option) => option.value === approval?.placeOfServiceTermId,
              ) || null
            }
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              chartContext.onChange({
                approval: {
                  [approval?.id]: {
                    placeOfServiceTermId: newValue?.value || null,
                  },
                },
              });
              setCardChart({
                ...cardChart,
                placeOfServiceTermId: newValue?.value || null,
              });
            }}
            disabled={originalOrderActivity?.isApproved === true}
            renderInput={(params) => (
              <MUITextField
                {...params}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="standard"
                label="Place of Service"
                fullWidth
                error={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.placeOfServiceTermId
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.placeOfServiceTermId
                    ? chartContext.validationIssues[className]?.approval?.[
                        approval?.id
                      ]?.placeOfServiceTermId
                    : ""
                }
              />
            )}
          />
        </MUIGrid>
      )
    );
  };

  const renderCommissionTierDropdown = () => {
    const getCommissionTierOptions = () => {
      return Term.sort(Object.values(mainContext.termsIndexed))
        .filter((term) => term.type === "commission_tier")
        .map((term) => ({
          label: term.name,
          value: term.id,
        }));
    };
    return (
      showCommissionTier && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIAutocomplete
            options={getCommissionTierOptions()}
            value={
              getCommissionTierOptions().find(
                (option) => option.value === approval?.commissionTierTermId,
              ) || null
            }
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              chartContext.onChange({
                approval: {
                  [approval?.id]: {
                    commissionTierTermId: newValue?.value || null,
                  },
                },
              });
              setCardChart({
                ...cardChart,
                commissionTierTermId: newValue?.value || null,
              });
            }}
            disabled={originalOrderActivity?.isApproved === true}
            renderInput={(params) => (
              <MUITextField
                {...params}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="standard"
                label="Commission/Tier"
                fullWidth
                error={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.commissionTierTermId
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.commissionTierTermId
                    ? chartContext.validationIssues[className]?.approval?.[
                        approval?.id
                      ]?.commissionTierTermId
                    : ""
                }
              />
            )}
          />
        </MUIGrid>
      )
    );
  };

  const renderEvaluationTypeDropdown = () => {
    const getEvaluationTypeOptions = () => {
      return Term.sort(Object.values(mainContext.termsIndexed))
        .filter((term) => term.type === "evaluation_type")
        .map((term) => ({
          label: term.name,
          value: term.id,
        }));
    };

    return (
      showEvaluationType && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIAutocomplete
            options={getEvaluationTypeOptions()}
            value={
              getEvaluationTypeOptions().find(
                (option) => option.value === approval?.evaluationTypeTermId,
              ) || null
            }
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              chartContext.onChange({
                approval: {
                  [approval?.id]: {
                    evaluationTypeTermId: newValue?.value || null,
                  },
                },
              });
              setCardChart({
                ...cardChart,
                evaluationTypeTermId: newValue?.value || null,
              });
            }}
            disabled={originalOrderActivity?.isApproved === true}
            renderInput={(params) => (
              <MUITextField
                {...params}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="standard"
                label="Evaluation Type"
                fullWidth
                error={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.evaluationTypeTermId
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.evaluationTypeTermId
                    ? chartContext.validationIssues[className]?.approval?.[
                        approval?.id
                      ]?.evaluationTypeTermId
                    : ""
                }
              />
            )}
          />
        </MUIGrid>
      )
    );
  };

  const renderAuthNumberTextfield = () => {
    return (
      showAuthNumber && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUITextField
            value={approval && approval.authNumber ? approval.authNumber : ""}
            size="small"
            label="Auth #"
            variant="standard"
            fullWidth={true}
            inputProps={{
              maxLength: 255,
            }}
            disabled={originalOrderActivity?.isApproved === true}
            InputLabelProps={{ shrink: true }}
            onBlur={(e) =>
              chartContext.onChange({
                approval: {
                  [approval?.id]: { authNumber: e.target.value.trim() },
                },
              })
            }
            onChange={(e) => {
              chartContext.onChange({
                approval: { [approval?.id]: { authNumber: e.target.value } },
              });
              setCardChart({ ...cardChart, authNumber: e.target.value });
            }}
            error={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.authNumber
                ? true
                : false
            }
            helperText={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.authNumber
                ? chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.authNumber
                : ""
            }
          />
        </MUIGrid>
      )
    );
  };

  const renderPredeterminationNumberTextfield = () => {
    return (
      showPredeterminationNumber && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUITextField
            value={approval?.predeterminationNumber || ""}
            size="small"
            label="Predetermination #"
            variant="standard"
            fullWidth
            inputProps={{
              maxLength: 255,
            }}
            disabled={originalOrderActivity?.isApproved === true}
            InputLabelProps={{ shrink: true }}
            onBlur={(e) =>
              chartContext.onChange({
                approval: {
                  [approval?.id]: {
                    predeterminationNumber: e.target.value.trim(),
                  },
                },
              })
            }
            onChange={(e) => {
              chartContext.onChange({
                approval: {
                  [approval?.id]: { predeterminationNumber: e.target.value },
                },
              });
              setCardChart({
                ...cardChart,
                predeterminationNumber: e.target.value,
              });
            }}
          />
        </MUIGrid>
      )
    );
  };

  const renderRentalOrSameSimRadioGroup = () => {
    return (
      showRentalOrSameSim && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIFormControl
            error={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.activeRentalSameOrSimilar
                ? true
                : false
            }
            helperText={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.activeRentalSameOrSimilar
                ? chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.activeRentalSameOrSimilar
                : ""
            }
          >
            <MUIFormLabel
              disabled={originalOrderActivity?.isApproved === true}
              sx={{ mb: 0 }}
            >
              Active Rental Same/Sim?
            </MUIFormLabel>
            <MUIRadioGroup
              row
              value={
                approval?.activeRentalSameOrSimilar !== undefined
                  ? approval?.activeRentalSameOrSimilar
                  : null
              }
              onChange={(e) => {
                chartContext.onChange({
                  approval: {
                    [approval?.id]: {
                      activeRentalSameOrSimilar: e.target.value === "true",
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  activeRentalSameOrSimilar: e.target.value === "true",
                });
              }}
            >
              <MUIFormControlLabel
                value={true}
                control={<MUIRadio size="small" />}
                label="Yes"
                disabled={originalOrderActivity?.isApproved === true}
              />
              <MUIFormControlLabel
                value={false}
                control={<MUIRadio size="small" />}
                label="No"
                disabled={originalOrderActivity?.isApproved === true}
              />
            </MUIRadioGroup>
          </MUIFormControl>
        </MUIGrid>
      )
    );
  };

  const renderFinancialHardshipRadioGroup = () => {
    return (
      showFinancialHardship && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIFormControl
            error={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.financialHardship
                ? true
                : false
            }
            helperText={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.financialHardship
                ? chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.financialHardship
                : ""
            }
          >
            <MUIFormLabel
              disabled={originalOrderActivity?.isApproved === true}
              sx={{ mb: 0 }}
            >
              Financial Hardship?
            </MUIFormLabel>
            <MUIRadioGroup
              row
              value={
                approval?.financialHardship !== undefined
                  ? approval?.financialHardship
                  : null
              }
              onChange={(e) => {
                chartContext.onChange({
                  approval: {
                    [approval?.id]: {
                      financialHardship: e.target.value === "true",
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  financialHardship: e.target.value === "true",
                });
              }}
            >
              <MUIFormControlLabel
                value={true}
                control={<MUIRadio size="small" />}
                label="Yes"
                disabled={originalOrderActivity?.isApproved === true}
              />
              <MUIFormControlLabel
                value={false}
                control={<MUIRadio size="small" />}
                label="No"
                disabled={originalOrderActivity?.isApproved === true}
              />
            </MUIRadioGroup>
          </MUIFormControl>
        </MUIGrid>
      )
    );
  };

  const renderRentalOrPurchaseRadioGroup = () => {
    return (
      showRentalOrPurchase && (
        <MUIGrid item md={4} xs={12} sx={getContainerQuery(600)}>
          <MUIFormControl
            error={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.rental
                ? true
                : false
            }
            helperText={
              chartContext.validationIssues[className]?.approval?.[approval?.id]
                ?.rental
                ? chartContext.validationIssues[className]?.approval?.[
                    approval?.id
                  ]?.rental
                : ""
            }
          >
            <MUIFormLabel
              disabled={originalOrderActivity?.isApproved === true}
              sx={{ mb: 0 }}
            >
              Rental/Purchase?
            </MUIFormLabel>
            <MUIRadioGroup
              row
              value={approval?.rental !== undefined ? approval?.rental : null}
              onChange={(e) => {
                chartContext.onChange({
                  approval: {
                    [approval?.id]: { rental: e.target.value === "true" },
                  },
                });
                setCardChart({
                  ...cardChart,
                  rental: e.target.value === "true",
                });
              }}
            >
              <MUIFormControlLabel
                value={true}
                control={<MUIRadio size="small" />}
                label="Rental"
                disabled={originalOrderActivity?.isApproved === true}
              />
              <MUIFormControlLabel
                value={false}
                control={<MUIRadio size="small" />}
                label="Purchase"
                disabled={originalOrderActivity?.isApproved === true}
              />
            </MUIRadioGroup>
          </MUIFormControl>
        </MUIGrid>
      )
    );
  };

  const renderBillingNoteTextfield = () => {
    const handleBillingNoteChange = (updatedFields) => {
      if (billingNote && billingNote.id > 0) {
        chartContext.onChange({
          note: {
            [billingNote.id]: {
              text: updatedFields.billingNote,
            },
          },
        });
      } else if (updatedFields.billingNote.trim() !== "") {
        const note = {
          [newNoteId]: {
            id: newNoteId,
            accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
            activityId: chartContext.orderId,
            assignedToId: null,
            completed: true,
            createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
            createdAt: new Date().toISOString(),
            isAutoVerification: false,
            patientName: Patient.getDisplayName(patient),
            task: false,
            text: updatedFields.billingNote,
            type: "ActivityNote",
            noteTypeTermId: 88,
          },
        };
        chartContext.onChange({
          note: note,
        });
      }
      setCardChart({ ...cardChart, text: updatedFields.billingNote });
    };
    return (
      showBillingNote && (
        <MUIGrid item xs={12} sx={getContainerQuery(600)}>
          <MUITextField
            multiline
            rows={3}
            defaultValue={billingNote?.text || ""}
            fullWidth={true}
            InputLabelProps={{ shrink: true }}
            label={"Billing Note"}
            onChange={(e) => {
              handleBillingNoteChange({ billingNote: e.target.value });
            }}
            disabled={originalOrderActivity?.isApproved === true}
            variant="standard"
          />
        </MUIGrid>
      )
    );
  };

  const renderAlerts = () => {
    return (
      errorMessage && (
        <MUIGrid item xs={12} sx={{ mt: 1 }}>
          <MUIAlert severity="error">{errorMessage}</MUIAlert>
        </MUIGrid>
      )
    );
  };

  /**
   * Renders the edit view displaying patient approval information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderEditView = () => {
    return (
      <MUIGrid
        container
        spacing={4}
        rowSpacing={3}
        sx={{ containerType: "inline-size" }}
      >
        {renderBillingLocationDropdown()}
        {renderAuthNumberTextfield()}
        {renderPredeterminationNumberTextfield()}
        {renderPlaceOfServiceDropdown()}
        {renderCommissionTierDropdown()}
        {renderEvaluationTypeDropdown()}
        {renderRentalOrSameSimRadioGroup()}
        {renderFinancialHardshipRadioGroup()}
        {renderRentalOrPurchaseRadioGroup()}
        {renderBillingNoteTextfield()}
        {renderAlerts()}
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying approval information.
   * @returns {JSX.Element} Grid layout with approval information display
   */
  const renderReadOnlyView = () => {
    const orderProducts = OrderActivity.getProducts(
      order,
      chartContext.originalChart,
    );
    const isApprovedUpgradedProductOrBaseCushionUpgrade =
      orderProducts.some(
        (product) =>
          product.approved === true && product.upgradedFromProductId !== null,
      ) || approval?.unapprovedUpgrade === true;

    return (
      <MUIBox>
        <MUIGrid container spacing={props.sidebar ? 1 : 4} rowSpacing={2}>
          <MUIGrid
            item
            xs={props.sidebar ? 6 : "auto"}
            sm={props.sidebar ? 12 : "auto"}
            md={props.sidebar ? 4 : "auto"}
          >
            <InfoItem
              label={"Approved On"}
              value={
                order?.approvalDate
                  ? moment(order?.approvalDate).format("M/D/YYYY")
                  : "None"
              }
            />
          </MUIGrid>
          {showBillingLocation && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Billing Location"}
                value={Location.getDisplayName(
                  mainContext.locationsIndexed[order?.billingLocationId],
                )}
              />
            </MUIGrid>
          )}
          {showAuthNumber && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Auth #"}
                value={approval?.authNumber || "None"}
              />
            </MUIGrid>
          )}
          {showPredeterminationNumber && approval?.predeterminationNumber && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Predetermination #"}
                value={approval.predeterminationNumber}
              />
            </MUIGrid>
          )}
          {showPlaceOfService && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Place of Service"}
                value={Term.getDisplayName(
                  mainContext.termsIndexed[approval?.placeOfServiceTermId],
                )}
              />
            </MUIGrid>
          )}
          {showCommissionTier && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Commission/Tier"}
                value={Term.getDisplayName(
                  mainContext.termsIndexed[approval?.commissionTierTermId],
                )}
              />
            </MUIGrid>
          )}
          {showEvaluationType && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <InfoItem
                label={"Evaluation Type"}
                value={Term.getDisplayName(
                  mainContext.termsIndexed[approval?.evaluationTypeTermId],
                )}
              />
            </MUIGrid>
          )}
        </MUIGrid>
        <MUIGrid
          container
          spacing={props.sidebar ? 1 : 2}
          rowSpacing={2}
          sx={{ mt: 1 }}
        >
          {showRentalOrSameSim && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <MUIChip
                label={"Active Rental Same/Sim"}
                color={
                  approval?.activeRentalSameOrSimilar ? "primary" : undefined
                }
                icon={
                  approval?.activeRentalSameOrSimilar === true ? (
                    <MUICheckCircleIcon />
                  ) : approval?.activeRentalSameOrSimilar === false ? (
                    <MUICancelIcon />
                  ) : (
                    <MUIHelpOutlineIcon />
                  )
                }
                size="small"
              />
            </MUIGrid>
          )}
          {showFinancialHardship && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <MUIChip
                label={"Financial Hardship"}
                color={approval?.financialHardship ? "primary" : undefined}
                icon={
                  approval?.financialHardship === true ? (
                    <MUICheckCircleIcon />
                  ) : approval?.financialHardship === false ? (
                    <MUICancelIcon />
                  ) : (
                    <MUIHelpOutlineIcon />
                  )
                }
                size="small"
              />
            </MUIGrid>
          )}
          {showRentalOrPurchase && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <MUIChip
                label={
                  approval?.rental === true
                    ? "Rental"
                    : approval?.rental === false
                      ? "Purchase"
                      : "Rental/Purchase"
                }
                color={
                  approval?.rental !== null && approval?.rental !== undefined
                    ? "primary"
                    : undefined
                }
                size="small"
                icon={
                  approval?.rental !== null &&
                  approval?.rental !== undefined ? null : (
                    <MUIHelpOutlineIcon />
                  )
                }
              />
            </MUIGrid>
          )}
          {showBaseCushionUpgrade && (
            <MUIGrid
              item
              xs={props.sidebar ? 6 : "auto"}
              sm={props.sidebar ? 12 : "auto"}
              md={props.sidebar ? 4 : "auto"}
            >
              <MUIChip
                label={"Base/Cushion Upgrade"}
                color={
                  isApprovedUpgradedProductOrBaseCushionUpgrade
                    ? "primary"
                    : undefined
                }
                size="small"
                icon={
                  isApprovedUpgradedProductOrBaseCushionUpgrade === true ? (
                    <MUICheckCircleIcon />
                  ) : isApprovedUpgradedProductOrBaseCushionUpgrade ===
                    false ? (
                    <MUICancelIcon />
                  ) : (
                    <MUIHelpOutlineIcon />
                  )
                }
              />
            </MUIGrid>
          )}
        </MUIGrid>
        <MUIGrid
          container
          spacing={props.sidebar ? 0 : 4}
          rowSpacing={2}
          sx={{ mt: 1, width: "100%", flexWrap: "wrap" }}
        >
          {showBillingNote && (
            <MUIGrid
              item
              xs={12}
              sx={{ width: "100%", overflow: "visible", flexGrow: 1 }}
            >
              <MUITypography variant="subtitle2">Billing Note</MUITypography>
              <MUITypography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  width: "100%",
                  overflowWrap: "anywhere",
                  minHeight: "1.5em",
                  display: "block",
                }}
              >
                {billingNote?.text || "None"}
              </MUITypography>
            </MUIGrid>
          )}
        </MUIGrid>
      </MUIBox>
    );
  };

  /**
   * Determines which view to render based on edit mode state. Renders search
   * box, add note field, notes table, or empty state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const originalOrderActivityProducts = Object.values(
      chartContext.originalChart.orderActivityProducts,
    )
      .filter(
        (orderActivityProduct) =>
          orderActivityProduct.productsId &&
          orderActivityProduct.orderActivityId === chartContext.orderId,
      )
      .map(
        (orderActivityProduct) =>
          chartContext.originalChart.product[orderActivityProduct.productsId],
      );
    const anyApprovedProducts =
      originalOrderActivityProducts.filter(
        (product) => product.approved === true,
      ).length > 0;

    const handleApproveClick = () => {
      // Sets the rules for validation without using chartContext validationRules state.
      const rules = { [validationRules.key]: validationRules.rules };

      if (!Security.hasPermission("order.approve")) {
        setErrorMessage("You do not have permission to approve orders.");
        setEditMode(true);
        return;
      } else {
        setErrorMessage(null);
      }
      if (!allowApprovalOnNoApprovedProducts && anyApprovedProducts === false) {
        setErrorMessage("Must approve at least one product.");
        setEditMode(true);
        return;
      } else {
        setErrorMessage(null);
      }
      const validateChart = chartContext.validateChartFields(rules);
      if (Object.values(validateChart || {}).length === 0) {
        const products = getProductsOnCurrentOrder();
        const noteText = buildNoteText(products, approval);
        const newNote = {
          [newApprovalNoteId]: {
            id: newApprovalNoteId,
            accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
            activityId: chartContext.orderId,
            assignedToId: null,
            completed: true,
            createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
            createdAt: new Date().toISOString(),
            dueDate: null,
            isAutoVerification: false,
            patientName: Patient.getDisplayName(patient),
            task: false,
            text: noteText,
            type: "ActivityNote",
          },
        };
        setCardChart({ ...cardChart, approvalNote: newNote });
        chartContext.onChange({ note: newNote });
        const orderActivityUpdate = {
          isApproved: true,
          ...(!order?.approvalDate
            ? { approvalDate: new Date().toISOString() }
            : {}),
        };
        chartContext.onChange({
          orderActivity: { [chartContext.orderId]: orderActivityUpdate },
        });
        setCardChart({ ...cardChart, ...orderActivityUpdate });
        setApproveClicked(true);
      }
    };

    const handleCancel = () => {
      const updatedOrderActivity = { ...order };
      const updatedApproval = { ...approval };
      const updatedBillingNote = { ...billingNote };
      const approvalFields = cardChart ? Object.keys(cardChart) : [];
      const updatedChart = { ...chartContext.chart };

      for (const field of approvalFields) {
        if (order?.[field] !== originalOrderActivity?.[field]) {
          updatedOrderActivity[field] = originalOrderActivity?.[field];
        }
        if (approval?.[field] !== originalApproval?.[field]) {
          updatedApproval[field] = originalApproval?.[field];
        }
        if (
          billingNote &&
          billingNote.id > 0 &&
          billingNote?.[field] !== originalBillingNote?.[field]
        ) {
          updatedBillingNote[field] = originalBillingNote?.[field];
          chartContext.onChange({
            note: { [billingNote.id]: { ...updatedBillingNote } },
          });
        } else {
          delete updatedChart.note[newNoteId];
          delete updatedChart.note[newApprovalNoteId];
          chartContext.onChange({ note: { ...updatedChart.note } });
        }
      }

      chartContext.onChange({
        orderActivity: { [chartContext.orderId]: { ...updatedOrderActivity } },
        approval: { [approval?.id]: { ...updatedApproval } },
      });

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
      chartContext.updateValidationRules({ key: className, rules: [] });
      setApproveClicked(false);
      setUnapproveClicked(false);
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);
      setErrorMessage(null);
    };

    return [
      ...(editMode
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
          ]
        : [
            originalOrderActivity?.isApproved === true ? (
              <>
                {allowApproval && (
                  <MUIButton
                    variant="contained"
                    color="error"
                    disabled={chartContext.isUpdating}
                    onClick={() => {
                      chartContext.onChange({
                        orderActivity: {
                          [chartContext.orderId]: { isApproved: null },
                        },
                      });
                      setUnapproveClicked(true);
                    }}
                    startIcon={<MUIUnpublishedIcon />}
                    size="small"
                  >
                    Unapprove
                  </MUIButton>
                )}
              </>
            ) : (
              <>
                {allowApproval && (
                  <MUILoadingButton
                    variant="contained"
                    loading={chartContext.isUpdating}
                    disabled={chartContext.isUpdating}
                    onClick={() => {
                      handleApproveClick();
                    }}
                    startIcon={<MUICheckCircleIcon />}
                    size="small"
                  >
                    Approve
                  </MUILoadingButton>
                )}
                <MUIButton
                  disabled={chartContext.isUpdating}
                  onClick={() => setEditMode(true)}
                  size="small"
                >
                  Edit
                </MUIButton>
              </>
            ),
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartApproval.meta = { className: className, title: title, icon: icon };
